// Recovery from a stale build.
//
// The app is code-split, so a page holds an index.html that names chunk files by
// content hash. After a redeploy those hashes no longer exist, and the next
// lazy-loaded route fails: "Loading chunk … failed", or "Unexpected token '<'"
// when the host answers a missing .js with its index.html. The user sees a blank
// or half-rendered screen and can do nothing about it — reloading fixes it,
// because that fetches the new index.html.
//
// So reload, once. The guard flag makes a genuinely broken build fail visibly
// instead of looping: if the reload does not fix it, the error surfaces normally.

const RELOAD_FLAG = 'lingoStaleBuildReloadAt';
// Long enough that a second failure means the new build is broken too, short
// enough that a stale tab left open for days still recovers.
const RELOAD_COOLDOWN_MS = 60 * 1000;

export const isStaleBuildError = ({ message = '', name = '', filename = '' } = {}) => {
  const text = `${name} ${message}`;
  if (/ChunkLoadError|Loading chunk .* failed|Loading CSS chunk .* failed/i.test(text)) return true;
  // A missing hashed asset served as HTML: the parser hits the doctype.
  return /Unexpected token '<'/.test(text) && /\.(js|css|mjs)(\?|$)/i.test(filename);
};

export const recoverFromStaleBuild = () => {
  if (typeof window === 'undefined') return false;
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_FLAG)) || 0;
  } catch (_) {
    // Private mode / storage disabled: reloading blind is still better than a
    // blank screen, and the browser's own loop protection applies.
  }
  if (last && Date.now() - last < RELOAD_COOLDOWN_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch (_) { /* ignore */ }
  window.location.reload();
  return true;
};

export const __testables = { RELOAD_FLAG, RELOAD_COOLDOWN_MS };
